import React, { useEffect, useMemo, useState } from "react";
import "./ShareholderLettersManagement.css";
import {
  createShareholderLetter,
  deleteShareholderLetter,
  getShareholderLetters,
  updateShareholderLetter,
} from "../services/shareholderLetterService";

const emptyForm = {
  languageLabel: "",
  languageSlug: "",
  title: "",
  sectionLabel: "I.",
  status: "PUBLISHED",
  content: "",
};

const slugify = (value = "") =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const createFallbackIdentity = () => {
  const stamp = Date.now().toString();

  return {
    languageLabel: `Letter ${stamp.slice(-6)}`,
    languageSlug: `shareholder-letter-${stamp}`,
  };
};

const ShareholderLettersManagement = () => {
  const [letters, setLetters] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const summaryLabel = useMemo(() => {
    const publishedCount = letters.filter(
      (letter) => letter.status === "PUBLISHED"
    ).length;

    return `${letters.length} letters | ${publishedCount} published`;
  }, [letters]);

  const fetchLetters = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const res = await getShareholderLetters();
      const payload = res?.data ?? [];
      setLetters(Array.isArray(payload) ? payload : []);
    } catch (error) {
      console.error("Fetch shareholder letters failed:", error);
      setErrorMessage(
        error.response?.data?.message || "Failed to load shareholder letters."
      );
      setLetters([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLetters();
  }, []);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId("");
    setErrorMessage("");
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const buildPayload = () => {
    const title = formData.title.trim();
    const fallbackIdentity = createFallbackIdentity();
    const preservedLabel = formData.languageLabel.trim();
    const preservedSlug = slugify(formData.languageSlug);
    const derivedLabel = title || preservedLabel || fallbackIdentity.languageLabel;
    const derivedSlug =
      preservedSlug ||
      slugify(title) ||
      slugify(derivedLabel) ||
      fallbackIdentity.languageSlug;

    return {
      languageLabel: derivedLabel,
      languageSlug: derivedSlug,
      title,
      sectionLabel: formData.sectionLabel.trim() || "I.",
      status: formData.status,
      content: formData.content
        .split(/\r?\n\s*\r?\n/)
        .map((item) => item.trim())
        .filter(Boolean),
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();

    if (payload.content.length === 0) {
      setErrorMessage("Add at least one paragraph.");
      return;
    }

    try {
      setErrorMessage("");

      if (editingId) {
        await updateShareholderLetter(editingId, payload);
      } else {
        await createShareholderLetter(payload);
      }

      await fetchLetters();
      resetForm();
    } catch (error) {
      console.error("Save shareholder letter failed:", error);
      setErrorMessage(
        error.response?.data?.message || "Failed to save shareholder letter."
      );
    }
  };

  const handleEdit = (letter) => {
    setEditingId(letter.id);
    setErrorMessage("");
    setFormData({
      languageLabel: letter.languageLabel || "",
      languageSlug: letter.languageSlug || "",
      title: letter.title || "",
      sectionLabel: letter.sectionLabel || "I.",
      status: letter.status || "DRAFT",
      content: Array.isArray(letter.content) ? letter.content.join("\n\n") : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Delete this shareholder letter? This cannot be undone."
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteShareholderLetter(id);
      await fetchLetters();

      if (editingId === id) {
        resetForm();
      }
    } catch (error) {
      console.error("Delete shareholder letter failed:", error);
      setErrorMessage(
        error.response?.data?.message || "Failed to delete shareholder letter."
      );
    }
  };

  const handleToggleStatus = async (letter) => {
    const nextStatus = letter.status === "PUBLISHED" ? "DRAFT" : "PUBLISHED";

    try {
      setErrorMessage("");
      await updateShareholderLetter(letter.id, { status: nextStatus });
      await fetchLetters();

      if (editingId === letter.id) {
        setFormData((prev) => ({
          ...prev,
          status: nextStatus,
        }));
      }
    } catch (error) {
      console.error("Update shareholder letter status failed:", error);
      setErrorMessage(
        error.response?.data?.message || "Failed to update shareholder letter status."
      );
    }
  };

  return (
    <div className="shareholder-admin-page">
      <section className="shareholder-admin-hero">
        <div>
          <h1>Shareholder Letters</h1>
          <p>Create, edit, publish, and remove the language versions shown on the website.</p>
        </div>
        <div className="shareholder-admin-meta">{summaryLabel}</div>
      </section>

      <section className="shareholder-admin-shell">
        <article className="shareholder-admin-form-card">
          <div className="shareholder-admin-card-top">
            <h2>{editingId ? "Edit Letter" : "Create Letter"}</h2>
            <button type="button" className="shareholder-outline-btn" onClick={resetForm}>
              Clear Form
            </button>
          </div>

          <div className="shareholder-admin-form-grid">
            <label className="shareholder-admin-field">
              <span>Title</span>
              <input
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Optional internal title"
              />
            </label>

            <label className="shareholder-admin-field">
              <span>Section Label</span>
              <input
                name="sectionLabel"
                value={formData.sectionLabel}
                onChange={handleInputChange}
                placeholder="I."
              />
            </label>

            <label className="shareholder-admin-field">
              <span>Status</span>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="DRAFT">DRAFT</option>
                <option value="PUBLISHED">PUBLISHED</option>
              </select>
            </label>
          </div>

          <label className="shareholder-admin-field">
            <span>Content</span>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              placeholder={"Write paragraphs here.\n\nUse a blank line between paragraphs."}
            />
          </label>

          {errorMessage && <p className="shareholder-admin-error">{errorMessage}</p>}

          <div className="shareholder-admin-action-row">
            {editingId && (
              <button
                type="button"
                className="shareholder-outline-btn"
                onClick={resetForm}
              >
                Cancel
              </button>
            )}
            <button type="button" className="shareholder-primary-btn" onClick={handleSave}>
              {editingId ? "Update Letter" : "Create Letter"}
            </button>
          </div>
        </article>

        <article className="shareholder-admin-list-card">
          <div className="shareholder-admin-card-top">
            <h2>All Letters</h2>
          </div>

          <div className="shareholder-admin-list">
            {isLoading && <div className="shareholder-admin-empty">Loading letters...</div>}

            {!isLoading && letters.length === 0 && (
              <div className="shareholder-admin-empty">
                No shareholder letters yet. Create the first one here.
              </div>
            )}

            {!isLoading &&
              letters.map((letter) => (
                <div className="shareholder-admin-row" key={letter.id}>
                  <div className="shareholder-admin-row-main">
                    <h3>{letter.title || letter.languageLabel || "Untitled Letter"}</h3>
                    <p>Section: {letter.sectionLabel || "I."}</p>
                    <p>Paragraphs: {Array.isArray(letter.content) ? letter.content.length : 0}</p>
                  </div>

                  <div className="shareholder-admin-row-side">
                    <span
                      className={`shareholder-status-pill ${
                        letter.status === "PUBLISHED" ? "is-live" : "is-draft"
                      }`}
                    >
                      {letter.status}
                    </span>

                    <button
                      type="button"
                      className="shareholder-outline-btn"
                      onClick={() => handleEdit(letter)}
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      className="shareholder-outline-btn"
                      onClick={() => handleToggleStatus(letter)}
                    >
                      {letter.status === "PUBLISHED" ? "Move to Draft" : "Publish"}
                    </button>

                    <button
                      type="button"
                      className="shareholder-delete-btn"
                      onClick={() => handleDelete(letter.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </article>
      </section>
    </div>
  );
};

export default ShareholderLettersManagement;
